import type { DeviceSync } from "@workspace/api-client-react";
// Deep import of the pure policy module: the package root re-exports
// runtime API-client code, which node --test cannot load (extension-less
// generated imports). The subpath keeps this module node-testable.
import {
  collectSyncMedia,
  planEviction,
  rekeySyncMedia,
  resolveOfflineCacheMaxBytes,
  type CacheIndexEntry,
} from "@workspace/player-shell/offline-window";
import { mediaCacheUrlKey } from "./media-url-key.ts";

/**
 * Desktop media-cache size cap (`offlineCacheMaxMb`) — pure planning half.
 *
 * The filesystem shells (TV/mobile) enforce the cap in
 * `@workspace/player-shell/native` with exact byte counts. The desktop cache
 * is Cache Storage, where opaque (no-cors) responses expose no size, so this
 * module plans eviction over a best-effort byte index:
 *
 *  - entries fetched in CORS mode have exact sizes (recorded at prefetch);
 *  - opaque entries are estimated from `navigator.storage.estimate()` usage
 *    (the un-attributed remainder split across unknown-size entries — browsers
 *    pad opaque usage, so estimates skew high, which errs toward evicting
 *    sooner rather than overrunning the disk);
 *  - with no storage estimate at all, a flat per-entry fallback applies.
 *
 * Victim selection is the shared `planEviction` policy: never evict media the
 * current window still needs while optional entries remain; if required media
 * alone exceeds the cap, drop the latest-needed items first (they fall back
 * to streaming when online).
 */

/** Stored (IndexedDB) byte index: Cache-Storage URL key → observed size. */
export type StoredCacheIndex = Record<
  string,
  { bytes: number | null; cachedAt: number }
>;

/** Per-entry size guess when no storage estimate is available (opaque media). */
export const UNKNOWN_ENTRY_FALLBACK_BYTES = 10 * 1024 * 1024;

/**
 * Every Cache-Storage URL key the sync payload references, deduped, ordered by
 * need (base program first, then timed events by fire time) — the protected
 * set handed to `planEviction`.
 */
export function requiredUrlKeysInNeedOrder(sync: DeviceSync): string[] {
  // Shares rekeySyncMedia with the readiness pass, so the set eviction
  // protects and the set readiness counts are provably the same list.
  return rekeySyncMedia(collectSyncMedia(sync), (target) =>
    mediaCacheUrlKey(target.url),
  ).map((target) => target.key);
}

/** Size estimate for entries whose bytes were unobservable (opaque). */
export function estimateUnknownEntryBytes(opts: {
  knownTotalBytes: number;
  unknownCount: number;
  storageUsageBytes: number | null;
}): number {
  const { knownTotalBytes, unknownCount, storageUsageBytes } = opts;
  if (unknownCount <= 0) return 0;
  if (storageUsageBytes === null) return UNKNOWN_ENTRY_FALLBACK_BYTES;
  const remainder = storageUsageBytes - knownTotalBytes;
  if (remainder <= 0) return UNKNOWN_ENTRY_FALLBACK_BYTES;
  return Math.max(1, Math.ceil(remainder / unknownCount));
}

/**
 * Turn what is actually in Cache Storage plus the stored byte index into the
 * `CacheIndexEntry[]` shape `planEviction` consumes. Index entries for keys no
 * longer cached are ignored; cached keys missing from the index get the
 * unknown-size estimate and `cachedAt: 0` (oldest, so they are the first
 * optional victims).
 */
export function reconcileCacheIndex(opts: {
  cachedUrlKeys: readonly string[];
  storedIndex: StoredCacheIndex;
  storageUsageBytes: number | null;
}): CacheIndexEntry[] {
  const { cachedUrlKeys, storedIndex, storageUsageBytes } = opts;

  let knownTotalBytes = 0;
  let unknownCount = 0;
  for (const key of cachedUrlKeys) {
    const stored = storedIndex[key];
    if (stored && typeof stored.bytes === "number" && stored.bytes > 0) {
      knownTotalBytes += stored.bytes;
    } else {
      unknownCount++;
    }
  }
  const unknownBytes = estimateUnknownEntryBytes({
    knownTotalBytes,
    unknownCount,
    storageUsageBytes,
  });

  return cachedUrlKeys.map((key) => {
    const stored = storedIndex[key];
    const known =
      stored && typeof stored.bytes === "number" && stored.bytes > 0;
    return {
      key,
      bytes: known ? (stored.bytes as number) : unknownBytes,
      cachedAt: stored?.cachedAt ?? 0,
    };
  });
}

/**
 * Cache-Storage URL keys to delete so the media cache fits the device's
 * `offlineCacheMaxMb`. Pure: callers observe the cache/index/storage estimate
 * and apply the returned victims (see `enforceCacheCap` in offline-store.ts).
 */
export function planCacheCapEviction(
  sync: DeviceSync,
  observed: {
    cachedUrlKeys: readonly string[];
    storedIndex: StoredCacheIndex;
    storageUsageBytes: number | null;
  },
): string[] {
  const entries = reconcileCacheIndex(observed);
  const maxBytes = resolveOfflineCacheMaxBytes(sync.device?.settings);
  return planEviction(entries, maxBytes, requiredUrlKeysInNeedOrder(sync));
}
